import store from "../store";
export const isAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    return false;
  }

  const payload = JSON.parse(atob(token.split(".")[1]));
  const expirationTime = payload.exp * 1000;

  if (Date.now() >= expirationTime) {
    localStorage.removeItem("authToken");
    return false;
  }
  return true;
};

export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function adjustOrderDate(orderDate, closingHours) {
  const orderDateObj = new Date(orderDate);
  orderDateObj.setHours(orderDateObj.getHours() - closingHours);
  return orderDateObj;
}

export function getDateDifference(date1, date2) {
  const diffTime = Math.abs(new Date(date2) - new Date(date1)); // Get the absolute difference in milliseconds
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
  return diffDays;
}

export function isValidNumber(input, min = -Infinity, max = Infinity) {
  // Convert input to a number if it's a string and check if it's a valid number
  const number = typeof input === "string" ? Number(input) : input;

  // Check if the number is valid and within the specified range
  if (
    typeof number === "number" &&
    !isNaN(number) &&
    isFinite(number) &&
    number >= min &&
    number <= max
  ) {
    return true;
  }

  return false;
}

export function transformDigitalOrderItems({ items }) {
  const { items: itemsData } = store;
  const itemsMap = itemsData.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  return items.map((item) => {
    const { itemId, variationId } = item;
    const variantData =
      itemsMap[parseInt(itemId)].variant.find(
        (variantItem) => variantItem.id == variationId
      ) || false;
    const itemData = itemsMap[parseInt(itemId)];

    return {
      itemId: itemData.id,
      id: itemData.id,
      variantId: variantData ? variantData.id : "",
      name: itemData.name,
      variantName: variantData ? variantData.varientName : "",
      price: variantData ? variantData.varientAmount : itemData.amount,
      quantity: item.quantity,
      feUniqueItemId:
        itemData.id + (variantData ? `_${variantData.id}` : "") + "_online",
      extra: false,
      isVariant: variantData ? true : false,
      variant: variantData ? variantData.varientName : "",
      createdAt: new Date(),
      readyAt: false,
    };
  });
}

/**
 * Maps a source order object to the target order structure
 * @param {Object} sourceOrder - The source order object
 * @returns {Object} - The transformed order object
 */

// example
// {
//   amount: 50.6,
//   billNo: "2",
//   billStatus: "pending",
//   canceled: false,
//   createdAt: "2025-05-17T07:58:50.925Z",
//   customerName: "shubham",
//   customerPhone: "9004318666",
//   discount: 0,
//   items: [
//     {
//       createdAt: "2025-05-17T13:28:51.891Z",
//       feUniqueItemId: "4388_testId_io",
//       id: "ae8ddfd1-d4bc-4022-9a7e-c7166f3bf6ad",
//       isExtra: true,
//       isVariant: false,
//       itemMenuId: "extra",
//       name: "biryani-333",
//       price: "50.60",
//       quantity: 1,
//       status: "preparing",
//       updatedAt: "2025-05-17T13:28:51.891Z",
//       variantId: "NA",
//       variantName: "NA"
//     }
//   ],
//   orderId: "07b3e6e8-3c35-4244-8d13-f25cbd58a895",
//   orderStatus: "accepted",
//   orderType: "physical",
//   partialAmoutsplit: [],
//   paymentmethod: "NA",
//   staffId: "NA",
//   status: "KOTPrint",
//   tableVPA: "QRapid2r1t1@QRapidVPA",
//   tips: 0,
//   updatedAt: "2025-05-17T07:58:50.925Z"
// };

export function mapLiveOrderObjectToDashboardObject(sourceOrder) {
  // Map items with the new structure
  const mappedItems = sourceOrder.items.map((item) => {
    return {
      canceled: item.status === "cancelled" || false,
      createdAt: new Date(item.createdAt),
      extra: item.isExtra || false,
      feUniqueItemId: item.feUniqueItemId || `${item.id}_${item.variantId}`,
      id: item.id,
      isVariant: item.isVariant || false,
      itemMenuId: item.itemMenuId,
      name: item.name,
      price: parseFloat(item.price),
      quantity: item.quantity,
      status: item.status,
      variantId: item.variantId || "NA",
      variantName: item.variantName || "NA",
      variation: item.variantName || "NA",
      comment:
        item.itemPreparationNote === "NA" ? "" : item.itemPreparationNote,
      updatedAt: item.updatedAt,
      addOns: item.addOns || item.addons || item.addOn || [],
      addons: item.addOns || item.addons || item.addOn || [],
      addOn: item.addOns || item.addons || item.addOn || [],
    };
  });

  let orderStatus = "VACANT";

  if (sourceOrder.orderStatus === "requested") {
    orderStatus = "RequestKOT";
  } else if (
    ["served", "in-process", "partially-served", "accepted"].includes(
      sourceOrder.orderStatus
    )
  ) {
    orderStatus = "KOTPrint";
  } else if (sourceOrder.orderStatus === "bill-requested")
    orderStatus = sourceOrder.orderStatus;

  // Create the transformed order object
  return {
    address: sourceOrder.customerAddress,
    amount: String(sourceOrder.amount),
    billTxnLogs: sourceOrder.billTxnLogs,
    billNo: parseInt(sourceOrder.billNo, 10),
    createdAt: sourceOrder.createdAt,
    customer: {
      address: sourceOrder.customerAddress || "NA",
      name: sourceOrder.customerName || "NA",
      phone: sourceOrder.customerPhone || "NA",
    },
    items: mappedItems,
    name: sourceOrder.customerName || "NA",
    onlineOrderCode: "",
    orderComment: "",
    orderID: sourceOrder.orderId,
    orderType: sourceOrder.orderType,
    phone: sourceOrder.customerPhone || "NA",
    platformName: "",
    staffId: sourceOrder.staffId || "",
    status: orderStatus,
    tableVPA: sourceOrder.tableVPA,
    typeOfOrder: sourceOrder.typeOfOrder,
    updatedAt: sourceOrder.updatedAt,
    orderPreparationNote:
      sourceOrder.orderPreparationNote === "NA"
        ? ""
        : sourceOrder.orderPreparationNote,
  };
}

export const getFormatedDate = (date) => {
  const day = date.getDate().toString().padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 -> 12 for 12 AM
  hours = hours.toString().padStart(2, "0");

  const formattedDateTime = `${day} ${month} ${year} - Time : ${hours}:${minutes} ${period}`;
  return formattedDateTime
};

export const playRing = (audio) => {
  const ring = new Audio(audio);
  ring.volume = 1.0;
  ring.play().catch((error) => {
    console.error("Playback failed:", error);
  });
};


/**
 * Determines the applicable price based on room, table, and time context
 * @param {Array} rulesArray - Array of pricing rule objects
 * @param {Object} context - The context containing room, table, and time
 * @returns {number|null} - The applicable price or null if no rule matches
 */
export function getRulePrice(rulesArray, context) {
  const { room, table, time } = context;
  const currentTime =
    time instanceof Date ? time.getHours() * 60 + time.getMinutes() : null;

  let applicablePrice = null;

  // Filter for active rules only
  const activeRules = rulesArray.filter(rule => rule.status === "active");

  for (const rule of activeRules) {
    // Skip if room doesn't match (unless rule.room is "*")
    if (rule.room !== "*" && rule.room !== room) continue;
    
    // Skip if table doesn't match (unless rule.table 
    if (rule.table !== "*" && rule.table !== table) continue;

    // Handle time constraints
    if (rule.startTime !== "*" && currentTime !== null) {
      const ruleStartTime = parseTime(rule.startTime);
      
      // If there's an end time, check if current time is within range
      if (rule.endTime) {
        const ruleEndTime = parseTime(rule.endTime);
        if (currentTime < ruleStartTime || currentTime > ruleEndTime) continue;
      } 
      // If no end time but start time exists, just check if current time is after start time
      else if (currentTime < ruleStartTime) {
        continue;
      }
    }

    // If we've made it here, the rule applies
    applicablePrice = parseFloat(rule.price);
    break; // Use the first matching rule
  }

  return applicablePrice;

  function parseTime(timeStr) {
    if (!timeStr || timeStr === "*") return null;
    
    const [time, meridian] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (meridian === "PM" && hours !== 12) hours += 12;
    if (meridian === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes;
  }
}


export function transformItem(input) {
  return {
    id: input.id || '', // fallback to generated UUID if empty
    categoryId: input.categoryId || "", // fallback if needed
    itemName: input.itemName || "",
    description: input.description || "",
    images: input.images || [],
    price: input.price || 0,
    rating: input.rating || 0,
    restaurantCode: input.restaurantCode || "NA",
    rank: input.rank || 0,
    itemType: {
      veg: !!input.itemType?.veg,
      nonVeg: !!input.itemType?.nonVeg,
      spicy: !!input.itemType?.spicy,
      chefSpecial: !!input.itemType?.chefSpecial,
      bestSeller: !!input.itemType?.bestSeller,
    },
    outOfStock: !!input.outOfStock,
    status: input.status || "active",
    variation: (input.variation || []).map(v => ({
      id: v.id || '',
      name: v.name || "",
      price: v.price || 0,
      image: v.image || "",
      status: v.status || "active",
      priceRule: v.priceRule || [],
    })),
    addOn: input.addOns || [],
    priceRule: input.priceRule || [],
    itemSuggestion: input.itemSuggestion || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/** Stable cart line id: same rules for new items and items loaded from server. */
export function buildFeUniqueItemId(itemId, variantId, addOns = []) {
  const isVariant = !!(
    variantId &&
    variantId !== "undefined" &&
    variantId !== "NA" &&
    variantId !== ""
  );
  const variantSegment = isVariant ? String(variantId) : "undefined";
  const addonSegment = (addOns || []).map((a) => a?.id).filter(Boolean).join("_");
  return `${itemId}_${variantSegment}_addons_${addonSegment}`;
}

/** API expects compact add-on refs, not full menu objects. */
export function formatAddOnsForOrderAPI(addOns = []) {
  return (addOns || [])
    .map((addon) => ({
      id: addon?.id ?? addon?.addonId,
      quantity: Number(addon?.quantity) > 0 ? Number(addon.quantity) : 1,
    }))
    .filter((addon) => addon.id != null && addon.id !== "");
}

/** Shape used by POST /orders items and PATCH addItem updates. */
export function formatCartItemForOrderAPI(item) {
  const addOns = formatAddOnsForOrderAPI(
    item?.addOn || item?.addOns || item?.addons || []
  );
  const variantId = item?.variantId ?? item?.varientid;
  const hasVariant = !!(
    variantId &&
    variantId !== "undefined" &&
    variantId !== "NA" &&
    variantId !== ""
  );

  const payload = {
    itemId: item?.id ?? item?.itemId,
    quantity: Number(item?.quantity) || 1,
    name: String(item?.name ?? ""),
    price: Number(item?.price) || 0,
    extra: !!item?.extra,
    isVariant: item?.isVariant != null ? !!item.isVariant : hasVariant,
    itemMenuId: String(item?.itemMenuId ?? item?.id ?? item?.itemId ?? ""),
  };

  if (hasVariant) {
    payload.variantId = String(variantId);
    payload.varientid = String(variantId);
    if (item?.variantName) {
      payload.variantName = String(item.variantName);
    }
  }

  if (addOns.length > 0) {
    payload.addOns = addOns;
    payload.addons = addOns;
  }

  return payload;
}

/** Assign sequential batch index expected by order PATCH API. */
export function withOrderPatchSequence(updates = []) {
  return updates.map((update, index) => ({
    ...update,
    order: index,
  }));
}

// UUID Generation Function
export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};